import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { belowArtworkInnerStyles, belowArtworkStyles } from './headerLayoutStyles';
import { classes } from './styles';

interface HeaderProps {
  title: string;
  titlePosition?: 'over' | 'below';
  location?: string;
  employmentStatus?: string;
  salaryInfo?: string;
  fontFamily?: string;
  backgroundImageUrl?: string;
  backgroundImageUrls?: string[];
  foregroundImageUrls?: string[];
  fields?: Record<string, { visible?: boolean; value?: string | number | null }>;
}

const CAROUSEL_INTERVAL_MS = 5000;
const FADE_THROUGH_DURATION_MS = 300;
const FADE_THROUGH_HOLD_MS = 80;
const FADE_THROUGH_START_DELAY_MS = 16;
const FOREGROUND_FADE_DURATION_MS = 360;
const FOREGROUND_SWAP_PAUSE_MS = 32;

interface HeaderDetail {
  key: string;
  value: string;
}

interface HeaderFrameProps {
  title: string;
  titlePosition: 'over' | 'below';
  fontFamily?: string;
  backgroundImages: string[];
  foregroundImages: string[];
  details: HeaderDetail[];
}

// TODO: Does not handle suffixes (200k-1M), "to" separators (10000 to 20000),
// or complex strings (10000 - 20000 for remote, 20000 - 30000 for US developers).
const parseSalaryNumber = (salary: string): number | null => {
  const numericValue = parseInt(salary, 10);

  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatSalaryNumber = (salary?: string): string => {
  if (!salary) return '';

  const numericValue = parseSalaryNumber(salary);

  return numericValue === null ? '' : `$${numericValue.toLocaleString()}`;
};

const formatSalaryRange = (salary: string): string => {
  const [min, max] = salary.split('-', 2);

  return [formatSalaryNumber(min), formatSalaryNumber(max)]
    .filter((salaryPart) => salaryPart.length > 0)
    .join('-');
};

const formatSalary = (salary: string): string => {
  const cleanSalary = salary.replace(/[^0-9-]/g, '');
  const formattedSalary = cleanSalary.includes('-')
    ? formatSalaryRange(cleanSalary)
    : formatSalaryNumber(cleanSalary);

  return formattedSalary.length > 0 ? formattedSalary : salary;
};

const HeaderFrame: React.FC<HeaderFrameProps> = ({
  title,
  titlePosition,
  fontFamily,
  backgroundImages,
  foregroundImages,
  details
}) => {
  const usesForegroundCarousel = foregroundImages.length > 0;
  const carouselImages = usesForegroundCarousel ? foregroundImages : backgroundImages;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionImageIndex, setTransitionImageIndex] = useState<number | null>(null);
  const [isTransitionImageVisible, setIsTransitionImageVisible] = useState(false);
  const [isForegroundImageVisible, setIsForegroundImageVisible] = useState(true);
  const transitionTimeoutRefs = useRef<number[]>([]);
  const activeBackgroundImageUrl = usesForegroundCarousel
    ? backgroundImages[0]
    : (carouselImages[transitionImageIndex ?? activeImageIndex] ?? carouselImages[0]);
  const outgoingBackgroundImageUrl =
    transitionImageIndex === null ? undefined : carouselImages[activeImageIndex];
  const titleBelow = titlePosition === 'below';
  const hasBannerLayout = !titleBelow;
  const showArtwork = !titleBelow || backgroundImages.length > 0 || foregroundImages.length > 0;

  const clearTransitionTimers = useCallback(() => {
    transitionTimeoutRefs.current.forEach((timeout) => window.clearTimeout(timeout));
    transitionTimeoutRefs.current = [];
  }, []);

  useEffect(
    () => () => {
      clearTransitionTimers();
    },
    [clearTransitionTimers]
  );

  const startFadeThrough = useCallback(() => {
    const shouldSkipTransition = [carouselImages.length < 2, isTransitioning].some(Boolean);
    if (shouldSkipTransition) return;

    const nextImageIndex = (activeImageIndex + 1) % carouselImages.length;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setActiveImageIndex(nextImageIndex);
      return;
    }

    clearTransitionTimers();

    if (usesForegroundCarousel) {
      setIsTransitioning(true);
      setIsForegroundImageVisible(false);

      transitionTimeoutRefs.current = [
        window.setTimeout(() => {
          setActiveImageIndex(nextImageIndex);
        }, FOREGROUND_FADE_DURATION_MS),

        window.setTimeout(() => {
          setIsForegroundImageVisible(true);
        }, FOREGROUND_FADE_DURATION_MS + FOREGROUND_SWAP_PAUSE_MS),

        window.setTimeout(
          () => {
            setIsTransitioning(false);
          },
          FOREGROUND_FADE_DURATION_MS * 2 + FOREGROUND_SWAP_PAUSE_MS
        )
      ];

      return;
    }

    setIsTransitioning(true);
    setTransitionImageIndex(nextImageIndex);
    setIsTransitionImageVisible(true);

    transitionTimeoutRefs.current = [
      window.setTimeout(() => {
        setIsTransitionImageVisible(false);
      }, FADE_THROUGH_START_DELAY_MS),

      window.setTimeout(
        () => {
          setActiveImageIndex(nextImageIndex);
          setTransitionImageIndex(null);
          setIsTransitioning(false);
        },
        FADE_THROUGH_START_DELAY_MS + FADE_THROUGH_DURATION_MS + FADE_THROUGH_HOLD_MS
      )
    ];
  }, [
    activeImageIndex,
    carouselImages.length,
    clearTransitionTimers,
    isTransitioning,
    usesForegroundCarousel
  ]);

  useEffect(() => {
    if (carouselImages.length < 2) return undefined;

    const interval = window.setInterval(() => {
      startFadeThrough();
    }, CAROUSEL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [carouselImages.length, startFadeThrough]);

  const headerText = (
    <Box sx={classes.header.textContentSx(hasBannerLayout, !titleBelow && usesForegroundCarousel)}>
      <Typography
        component="h1"
        id="job-header-heading"
        sx={classes.header.headingSx(
          fontFamily,
          hasBannerLayout,
          !titleBelow && usesForegroundCarousel
        )}
      >
        {title}
      </Typography>
      {details.length > 0 && (
        <Typography
          component="div"
          sx={classes.header.detailsSx(
            fontFamily,
            hasBannerLayout,
            !titleBelow && usesForegroundCarousel
          )}
        >
          {details.map((detail, index) => (
            <React.Fragment key={detail.key}>
              {index > 0 && <span className="detail-separator">•</span>}
              <span>{detail.value}</span>
            </React.Fragment>
          ))}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      {showArtwork ? (
        <Box
          aria-labelledby="job-header-heading"
          component="section"
          data-active-image-index={activeImageIndex}
          data-testid={titleBelow ? 'wcag-header-artwork' : undefined}
          data-title-position={titleBelow ? 'below' : undefined}
          sx={
            titleBelow
              ? belowArtworkStyles(activeBackgroundImageUrl)
              : classes.header.containerSx(activeBackgroundImageUrl, true)
          }
        >
          {!usesForegroundCarousel
            ? backgroundImages.map((imageUrl) => (
                <img
                  key={`preload-${imageUrl}`}
                  alt=""
                  aria-hidden="true"
                  data-testid="wcag-header-preload-image"
                  src={imageUrl}
                  style={{ display: 'none' }}
                />
              ))
            : null}
          {isTransitioning && !usesForegroundCarousel && outgoingBackgroundImageUrl ? (
            <Box
              aria-hidden="true"
              data-testid="wcag-header-background-image"
              data-visible={isTransitionImageVisible ? 'true' : 'false'}
              sx={classes.header.backgroundImageSx(
                outgoingBackgroundImageUrl,
                isTransitionImageVisible
              )}
            />
          ) : null}
          <Box
            sx={
              titleBelow
                ? belowArtworkInnerStyles
                : classes.header.innerContainerSx(true, usesForegroundCarousel)
            }
          >
            {!titleBelow ? headerText : null}
            {usesForegroundCarousel ? (
              <Box
                aria-hidden="true"
                data-testid={titleBelow ? 'wcag-header-foreground-carousel' : undefined}
                sx={classes.header.foregroundCarouselSx()}
              >
                {foregroundImages.map((imageUrl, index) => {
                  const isActive = index === activeImageIndex;

                  return (
                    <Box
                      key={imageUrl}
                      alt=""
                      aria-hidden="true"
                      component="img"
                      data-active={isActive ? 'true' : 'false'}
                      data-testid="wcag-header-foreground-image"
                      data-visible={isActive && isForegroundImageVisible ? 'true' : 'false'}
                      decoding="async"
                      src={imageUrl}
                      sx={classes.header.foregroundImageSx(isActive, isForegroundImageVisible)}
                    />
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
      {titleBelow ? <Box sx={classes.header.containerSx()}>{headerText}</Box> : null}
    </>
  );
};

export const Header: React.FC<HeaderProps> = ({
  title,
  titlePosition = 'over',
  location,
  employmentStatus,
  salaryInfo,
  fontFamily,
  backgroundImageUrl,
  backgroundImageUrls,
  foregroundImageUrls,
  fields
}) => {
  const backgroundImages = useMemo(
    (): string[] =>
      (backgroundImageUrls?.length ? backgroundImageUrls : [backgroundImageUrl]).filter(
        (imageUrl): imageUrl is string => Boolean(imageUrl)
      ),
    [backgroundImageUrl, backgroundImageUrls]
  );
  const foregroundImages = useMemo(
    (): string[] =>
      (foregroundImageUrls ?? []).filter((imageUrl): imageUrl is string => Boolean(imageUrl)),
    [foregroundImageUrls]
  );
  const artworkKey = useMemo(
    () => JSON.stringify({ backgroundImages, foregroundImages }),
    [backgroundImages, foregroundImages]
  );
  const formattedSalary = salaryInfo ? formatSalary(salaryInfo) : null;
  const details: HeaderDetail[] = fields
    ? Object.entries(fields)
        .filter(([, field]) => field?.visible && field.value !== null && field.value !== undefined)
        .map(([key, field]) => ({ key, value: String(field.value) }))
    : [
        { key: 'location', value: location },
        { key: 'employmentStatus', value: employmentStatus },
        { key: 'salaryInfo', value: formattedSalary }
      ].filter((detail): detail is HeaderDetail => Boolean(detail.value));

  return (
    <HeaderFrame
      key={artworkKey}
      backgroundImages={backgroundImages}
      details={details}
      fontFamily={fontFamily}
      foregroundImages={foregroundImages}
      title={title}
      titlePosition={titlePosition}
    />
  );
};

export default Header;
